import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useProviderFormSubmit } from './useProviderFormSubmit';
import { useProviderFormDelete } from './useProviderFormDelete';
import type { PEOProvider } from '@/types/peo.types';
import type { FormData } from '../types';

/**
 * Custom hook to manage the PEO provider form state and submission
 */
export const useProviderForm = (
  provider?: PEOProvider, 
  onSuccess?: () => void,
  onDeleteProvider?: (providerId: string) => Promise<boolean>,
  isDeleteInProgressProp: boolean = false
) => {
  const { handleFormSubmit, isSubmitting } = useProviderFormSubmit(
    provider,
    onSuccess
  );
  
  const { handleProviderDelete, isDeleteInProgress: isDeleteInProgressState } = useProviderFormDelete(
    provider?.id,
    provider?.name,
    onDeleteProvider,
    onSuccess
  );

  // Initialize form with provider data if available
  const form = useForm<FormData>({
    defaultValues: provider
      ? {
          ...provider,
          specialties: provider.specialties || [],
          states_served: provider.states_served || [],
          industries_served: provider.industries_served || [],
          medical_carriers: provider.medical_carriers || [],
          workers_comp_carriers: provider.workers_comp_carriers || [],
          physical_offices: provider.physical_offices || [],
          ancillary_carriers: provider.ancillary_carriers || [],
          rating: provider.rating || 0,
          years_in_business: provider.years_in_business || 0,
          min_employee_count: provider.min_employee_count || null,
          max_employee_count: provider.max_employee_count || null,
          mobile_app: provider.mobile_app === true,
          payroll_platform: provider.payroll_platform || '',
          k401_vendor: provider.k401_vendor || '',
          renewal_date: provider.renewal_date || null,
          allows_medical_carveout: provider.allows_medical_carveout === true,
          allows_ancillary_carveout: provider.allows_ancillary_carveout === true,
          allows_workers_comp_carveout: provider.allows_workers_comp_carveout === true,
          allows_epli_carveout: provider.allows_epli_carveout === true,
          allows_401k_carveout: provider.allows_401k_carveout === true,
          is_esac_accredited: provider.is_esac_accredited === true,
          irs_certified: provider.irs_certified === true,
          audited: provider.audited === true,
          fully_bonded_insured: provider.fully_bonded_insured === true,
          napeo_member: provider.napeo_member === true,
          bbb_accredited: provider.bbb_accredited === true,
          aicpa_soc: provider.aicpa_soc === true,
        }
      : {
          name: '',
          min_employee_count: null,
          max_employee_count: null,
          specialties: [],
          states_served: [],
          rating: 0,
          years_in_business: 0,
          is_esac_accredited: false,
          irs_certified: false,
          audited: false,
          fully_bonded_insured: false,
          napeo_member: false,
          bbb_accredited: false,
          aicpa_soc: false,
          industries_served: [],
          medical_carriers: [],
          workers_comp_carriers: [],
          physical_offices: [],
          mobile_app: false,
          payroll_platform: '',
          ancillary_carriers: [],
          k401_vendor: '',
          renewal_date: null,
          website: '',
          hq_state: '',
          certification_institute: '',
          allows_medical_carveout: false,
          allows_ancillary_carveout: false,
          allows_workers_comp_carveout: false,
          allows_epli_carveout: false,
          allows_401k_carveout: false,
          // Enterprise fields defaults
          enterprise_features_enabled: false,
          subscription_tier: 'basic' as const,
          subscription_status: 'active' as const,
          stripe_customer_id: '',
          stripe_subscription_id: '',
          referral_code: '',
          trial_ends_at: '',
          subscription_updated_at: '',
          // Enhanced matching fields defaults
          admin_fee_percentage_min: null,
          admin_fee_percentage_max: null,
          admin_fee_per_employee_min: null,
          admin_fee_per_employee_max: null,
          implementation_cost_min: null,
          implementation_cost_max: null,
          monthly_minimum_fee: null,
          pricing_model: null,
          pricing_transparency_level: 'standard' as const,
          average_implementation_days: null,
          average_quote_response_hours: null,
          dedicated_implementation_manager: false,
          white_glove_onboarding: false,
          transition_support_included: true,
          data_migration_included: true,
          client_retention_rate_1yr: null,
          client_retention_rate_3yr: null,
          customer_satisfaction_score: null,
          nps_score: null,
          compliance_track_record_score: null,
          client_growth_rate: null,
          preferred_client_size_min: null,
          preferred_client_size_max: null,
          excluded_industries_strict: [],
          geographic_focus_states: [],
          requires_minimum_revenue: false,
          minimum_annual_revenue: null,
          risk_tolerance_level: 'moderate' as const,
          handles_high_risk_industries: false,
          workers_comp_self_insured: false,
          medical_stop_loss_threshold: null,
          underwriting_requirements: [],
          credit_score_requirements: null,
          multi_state_payroll_expertise: false,
          union_experience: false,
          prevailing_wage_experience: false,
          international_payroll: false,
          executive_benefits_available: false,
          custom_benefit_design: false,
          wellness_programs_included: false,
          api_capabilities: [],
          integration_partnerships: [],
          custom_reporting_available: false,
          real_time_analytics: false,
          mobile_app_features: [],
          market_position: null,
          key_differentiators: [],
          primary_competitors: [],
          win_rate_vs_competitors: null,
          average_client_save_percentage: null,
          broker_commission_percentage: null,
          broker_bonus_structure: '',
          broker_support_level: 'standard' as const,
          provides_broker_training: false,
          joint_marketing_available: false,
          backup_payroll_provider: '',
          business_continuity_plan: false,
          iso_certifications: [],
          industry_awards: [],
          case_studies_available: false,
          references_provided: true,
          last_performance_review: null,
          profile_completion_score: 0,
          data_quality_score: 0,
          last_competitive_analysis: null,
          notes_internal: '',
        },
  });

  // Handle form submission
  const onSubmit = async (data: FormData) => {
    console.log('Form submitted with data:', data);
    const result = await handleFormSubmit(data);
    console.log('Form submission result:', result);
    return result;
  };

  // Handle delete action
  const handleDelete = async () => {
    await handleProviderDelete();
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
  } = form;

  return {
    form,
    register,
    control,
    errors,
    handleSubmit,
    onSubmit,
    handleDelete,
    setValue,
    getValues,
    watch,
    isSubmitting,
    isDeleteInProgress: isDeleteInProgressProp || isDeleteInProgressState
  };
};
